import React, { useState, useMemo } from 'react';
import { useAddNewCommunicationChannel, useAddNewFrequency, useCommunicationData, useUpdateCommunicationData } from '@/services/communication';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Save, X, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useMSGVersion } from '@/app/pages/msg/hooks/useMSGVersion';

type EditableCellProps = {
  value: string;
  onChange: (value: string) => void;
  isEditing: boolean;
  options?: string[];
  onAddNew?: (value: string) => Promise<void>;
  type?: 'text' | 'select';
};

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  onChange,
  isEditing,
  options = [],
  onAddNew,
  type = 'text',
}) => {
  const [newOption, setNewOption] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);

  if (!isEditing) {
    // For display mode, show a simple span with the value or a dash if empty
    // Clean up any HTML that might have been saved as a value
    const displayValue = (() => {
      if (!value) return '-';
      if (typeof value === 'string' && (value.includes('<select') || value.includes('<input'))) {
        return '-';
      }
      return value;
    })();

    return <span className="text-foreground inline-flex items-center min-h-[36px]">{displayValue}</span>;
  }

  if (type === 'select') {
    // Ensure we have a clean value (not HTML) for the select
    const cleanValue = typeof value === 'string' && (value.includes('<select') || value.includes('<input')) ? '' : value;

    return (
      <div className="space-y-2">
        <Select
          value={cleanValue}
          onValueChange={(val) => {
            if (val === 'add-new') {
              setShowAddNew(true);
            } else {
              // Only update if it's a valid option or empty string
              if ((options && options.includes(val)) || val === '') {
                onChange(val);
              }
            }
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
            <SelectItem value="add-new" className="text-primary font-medium">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4" /> Add New
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        {showAddNew && (
          <div className="flex gap-2">
            <Input
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              placeholder="Enter new option"
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (newOption.trim() && onAddNew) {
                  await onAddNew(newOption.trim());
                  onChange(newOption.trim());
                  setNewOption('');
                  setShowAddNew(false);
                }
              }}
            >
              Add
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowAddNew(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full"
      />
    </div>
  );
};

// Helper interface for grouped rows
interface CommunicationItem {
  id: string;
  ismsTopics: string;
  communicationTool: string;
  // These are just for TypeScript, actual values come from storedValues
  frequency?: string;
  communicationChannel?: string;
}

interface GroupedRow {
  rows: CommunicationItem[];
  ismsTopic: string;
  rowSpan: number;
}

interface CommunicationTableProps {
  isReadOnly?: boolean;
}

export const CommunicationTable: React.FC<CommunicationTableProps> = ({ isReadOnly = false }) => {
  const { data, isLoading, error } = useCommunicationData();
  const updateMutation = useUpdateCommunicationData();
  const addNewFrequency = useAddNewFrequency();
  const addNewChannel = useAddNewCommunicationChannel();
  const { incrementVersion } = useMSGVersion();

  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<Record<string, any>>({});

  // Get stored values or default to empty object
  const storedValues = data?.storedValues || {};

  // Special mapping for specific tool names that don't follow the standard pattern
  const SPECIAL_KEYS: Record<string, string> = {
    'Management Review Meeting': 'mrm',
    'Certification Bodies': 'cb',
    'Security Incidents': 'si',
    'Threat Intelligence': 'ti',
  };

  // Helper function to convert communication tool name to storedValues key format
  const getStoredKey = (toolName: string) => {
    // Check for special cases first
    if (SPECIAL_KEYS[toolName]) {
      return SPECIAL_KEYS[toolName];
    }

    // Convert to camelCase and remove spaces
    return toolName
      .toLowerCase()
      .replace(/\s+(\w)/g, (_, letter) => letter.toUpperCase())
      .replace(/[^a-zA-Z0-9]/g, '');
  };

  // Helper function to get stored value with correct key pattern
  const getStoredValue = (toolName: string, type: 'frequency' | 'channel') => {
    const baseKey = getStoredKey(toolName);
    const key = `${baseKey}${type === 'frequency' ? 'Frequency' : 'CommunicationChannel'}`;
    return storedValues[key] || '';
  };

  // Group rows by ISMS Topic and calculate row spans
  const { groupedRows, rowIndices } = useMemo(() => {
    if (!data?.tableData) return { groupedRows: [] as GroupedRow[], rowIndices: new Map<number, number>() };

    const groups: Record<string, any[]> = {};
    const indices = new Map<number, number>();
    let currentIndex = 0;

    // Group rows by ISMS Topic
    data.tableData.forEach((row, index) => {
      const topic = row.ismsTopics || 'Other';
      if (!groups[topic]) {
        groups[topic] = [];
      }
      groups[topic].push(row);
      indices.set(index, currentIndex);
      currentIndex++;
    });

    // Convert to array of groups with row spans
    const grouped = Object.entries(groups).map(([topic, rows]) => ({
      ismsTopic: topic,
      rows,
      rowSpan: rows.length
    }));

    return { groupedRows: grouped, rowIndices: indices };
  }, [data?.tableData]);

  const handleEdit = (index: number) => {
    if (!data) return;

    const row = data.tableData[index];
    const baseKey = getStoredKey(row.communicationTool);

    // Initialize editedData with current values from storedValues
    setEditedData({
      ...row,
      frequency: storedValues[`${baseKey}Frequency`] || '',
      communicationChannel: storedValues[`${baseKey}CommunicationChannel`] || ''
    });

    setEditingRow(index);
  };

  const handleSave = async (index: number) => {
    if (!data) return;

    const row = data.tableData[index];
    const updatedTableData = [...data.tableData];
    updatedTableData[index] = { ...row, ...editedData };

    // Create a copy of the current storedValues
    const updatedStoredValues = { ...storedValues };

    // Get the base key for this row's communication tool
    const baseKey = getStoredKey(row.communicationTool);

    // Update frequency and communication channel in storedValues if they were edited
    if (editedData.frequency !== undefined) {
      updatedStoredValues[`${baseKey}Frequency`] = editedData.frequency;
    }
    if (editedData.communicationChannel !== undefined) {
      updatedStoredValues[`${baseKey}CommunicationChannel`] = editedData.communicationChannel;
    }

    try {
      await updateMutation.mutateAsync({
        tableData: updatedTableData,
        storedValues: updatedStoredValues
      });
      await incrementVersion(
        'communication_update',
        `Updated communication data for ${row.communicationTool}`,
        {
          communicationTool: row.communicationTool,
          oldData: row,
          newData: editedData
        }
      );
      setEditingRow(null);
      setEditedData({});
      toast.success('Communication data updated successfully');
    } catch (error) {
      console.error('Failed to update communication data:', error);
      toast.error('Failed to update communication data');
    }
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditedData({});
  };

  const handleCellChange = (field: string, value: string) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddNewFrequency = async (frequency: string) => {
    try {
      await addNewFrequency.mutateAsync(frequency);
      await incrementVersion(
        'communication_update',
        `Updated communication data for ${frequency}`,
        {
          communicationTool: frequency,
          oldData: {},
          newData: { frequency }
        }
      );
      toast.success('New frequency added successfully');
    } catch (error) {
      toast.error('Failed to add new frequency');
    }
  };

  const handleAddNewChannel = async (channel: string) => {
    try {
      await addNewChannel.mutateAsync(channel);
      await incrementVersion(
        'communication_update',
        `Updated communication data for ${channel}`,
        {
          communicationTool: channel,
          oldData: {},
          newData: { channel }
        }
      );
      toast.success('New communication channel added successfully');
    } catch (error) {
      toast.error('Failed to add new communication channel');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive">
        Error loading communication data: {error.message}
      </div>
    );
  }

  if (!data) {
    return <div>No communication data available</div>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Communication Channels</h2>
        </div>
        <Table className='table'>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">ISMS Topic</TableHead>
              <TableHead>Communication Tool</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Communication Channel</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedRows.flatMap((group, groupIndex) =>
              group.rows.map((row, rowIndex) => {
                const isFirstInGroup = rowIndex === 0;
                const absoluteIndex = data.tableData.findIndex(
                  r => r.ismsTopics === row.ismsTopics &&
                    r.communicationTool === row.communicationTool &&
                    r.frequency === row.frequency &&
                    r.communicationChannel === row.communicationChannel
                );
                const isEditing = editingRow === absoluteIndex;

                return (
                  <TableRow key={`${groupIndex}-${rowIndex}`} className="group">
                    {isFirstInGroup ? (
                      <TableCell
                        rowSpan={group.rowSpan}
                        className="align-top border-r font-medium"
                      >
                        {group.ismsTopic}
                      </TableCell>
                    ) : null}
                    <TableCell className="font-medium">
                      {row.communicationTool}
                    </TableCell>
                    <TableCell>
                      <EditableCell
                        value={isEditing ? editedData.frequency || getStoredValue(row.communicationTool, 'frequency') : getStoredValue(row.communicationTool, 'frequency')}
                        onChange={(value) => handleCellChange('frequency', value)}
                        isEditing={isEditing}
                        type="select"
                        options={data.frequencyDropDown}
                        onAddNew={handleAddNewFrequency}
                      />
                    </TableCell>
                    <TableCell>
                      <EditableCell
                        value={isEditing ? editedData.communicationChannel || getStoredValue(row.communicationTool, 'channel') : getStoredValue(row.communicationTool, 'channel')}
                        onChange={(value) => handleCellChange('communicationChannel', value)}
                        isEditing={isEditing}
                        type="select"
                        options={data.communicationChannelDropDown}
                        onAddNew={handleAddNewChannel}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSave(absoluteIndex)}
                            disabled={updateMutation.isPending}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel()}
                            disabled={updateMutation.isPending}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : !isReadOnly && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(absoluteIndex)}
                          className="transition-opacity"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export default CommunicationTable;
